(ns manatee.admin.users.components
  "Main panel, views and components for the Users tab.
   This allows administrators and project owners to view, add and edit users."
  (:require
   [cljs-time.format :as time-format]
   [manatee.admin.components :as admin-components]
   [manatee.admin.handlers :as admin-handlers]
   [manatee.admin.subs :as admin-subs]
   [manatee.admin.users.handlers :as handlers]
   [manatee.admin.users.subs :as subs]
   [manatee.components.modal.handlers :as modal-handlers]
   [manatee.components.modal.subs :as modal-subs]
   [manatee.components.notifications.handlers :as notification-handlers]
   [manatee.spec.core :as spec]
   [manatee.user.subs :as user-subs]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [rothko.button.component :as button]
   [rothko.collection-components.core :as collection]
   [rothko.collection-components.table.component :as table]
   [rothko.form2.checkbox.component :as checkbox]
   [rothko.form2.component :as form]
   [rothko.form2.input.component :as input]
   [rothko.form2.radio.component :as radio]
   [rothko.form2.select.core :as select]
   [rothko.form2.select.single-select.component :as single-select]
   [rothko.form2.validation :as validation]
   [rothko.icon.component :as icon]
   [rothko.panel.component :as panel-component]))

(def ^:private time+date-formatter
  (time-format/formatter "HH:mm' GMT, 'dd/MMM/yyyy"))

(defn- role-change-confirmation-message
  [role]
  (str "● lose their current role and gain the role of " role))

(defn- queue-team-change-confirmation-message
  [queue-name]
  (str "● be unassigned from their current queue " queue-name))

(defn- update-user-confirmation-dialog
  "Ask for confirmation of a user role change and/or team."
  [id first-name last-name initial-role current-queue-team-id current-queue-team-name]
  (let [{:keys [role team] :as event} @(rf/subscribe [::modal-subs/modal-props])]
    [admin-components/confirmation-dialog {:id :confirm-update-user
                                           :class [:admin-page__confirmation-dialog]
                                           :ok-fn #(do (rf/dispatch [::handlers/update-user event id])
                                                       (rf/dispatch [::modal-handlers/hide :confirm-update-user]))
                                           :cancel-fn #(rf/dispatch [::modal-handlers/hide :confirm-update-user])}
     [:div
      [:p (str "In completing this action " first-name " " last-name " will:")]
      (when-not (= role initial-role)
        [:p (role-change-confirmation-message role)])
      (when-not (= team current-queue-team-id)
        [:p (queue-team-change-confirmation-message current-queue-team-name)])
      [:p "Are you sure?"]]]))

(defn- format-booleans
  [user]
  (-> user
      (update :is-evaluator #(when % "✓"))
      #_(update :is-active #(when % "✓"))
      (update :require-approval? #(when % "✓"))))

(defn- format-user-session-end
  [user]
  (update user
          :session-end #(if %
                          (time-format/unparse time+date-formatter %)
                          "No data")))

(defn- user-id-formatter
  "Format the user identifier to display only the first six characters and a
   clickable 'copy to clipboard' icon."
  [_props {:keys [id] :as _row-data}]
  (let [id-str (str id)
        label (re-find #"[a-fA-F0-9]{6}" id-str)
        on-click-handler (fn copy-id-to-clipboard
                           [event]
                           (.stopPropagation event)
                           (js/navigator.clipboard.writeText id-str)
                           (rf/dispatch [::notification-handlers/add-alert :main
                                         {:message "User id copied"
                                          :type :info}
                                         :auto-dismiss? true]))]
    [:span
     label
     " "
     [icon/icon {:icon :copy
                 :title "Copy Id to clipboard"
                 :on-click on-click-handler
                 :size 14}]]))

(defn- user-role-formatter
  [role-names _props {:keys [role] :as _row-data}]
  (get role-names
       (keyword role)))

(defn- user-active-formatter
  "Format the user active value to display a clickable, 'toggle value'
   indicator."
  [current-user-id _props {:keys [id is-active updating?] :as _row-data}]
  (let [logged-in? (= (str id) current-user-id)
        enabled? (and (not logged-in?) (not updating?))
        label (if updating? "…" (if is-active "✅" "❌"))
        title (if is-active "Deactivate user" "Activate user")
        on-click-handler (fn [event]
                           (.stopPropagation event)
                           (when enabled?
                             (rf/dispatch [::handlers/set-user-active? id (not is-active)])))]
    [:span (cond-> {:disabled (not enabled?)
                    :on-click on-click-handler}
             logged-in? (assoc :title "Cannot disable logged-in user!"
                               :style {:cursor :not-allowed})
             enabled? (assoc :title title
                             :style {:cursor :crosshair}))
     label]))

(defn- user-team-formatter
  [user-teams _props user]
  (:representation (first (filter #(= (:value %)
                                      (:team user))
                                  user-teams))))

(defn- format-user-cells
  [user]
  (-> user
      (format-booleans)
      (format-user-session-end)))

(defn- team-filter
  [user-teams user-team-filter]
  [:div {:key (count user-teams)}
   [:label "Filter by team"]
   [single-select/field
    (merge
     (admin-components/form-field-props :no-team-filter #(do (rf/dispatch [::admin-handlers/set-team-filter %]) (reset! user-team-filter %)))
     {::select/options (conj user-teams {:value :no-team-filter :representation "<No filter>"})})]])

(defn- inactive-filter
  [show-inactive-users?]
  [:div
   [:label {:for :show-inactive} "Show inactive users"]
   [:div
    [:input
     {:type :checkbox
      :name :show-inactive
      :on-change #(swap! show-inactive-users? not)
      :disabled false
      :defaultChecked false}]]])

(defn- user-form-component
  []
  (let [{:keys [id first-name last-name email role team is-active is-evaluator require-approval? created queue updating?] :as selected-user} @(rf/subscribe [::admin-subs/selected-user])
        current-user @(rf/subscribe [::user-subs/user])
        users @(rf/subscribe [::admin-subs/users])
        adding-user? @(rf/subscribe [::admin-subs/adding-user?])
        user-teams @(rf/subscribe [::admin-subs/user-teams])
        current-user-id (:id current-user)
        current-queue-team-id (get-in queue [:team :id] :no-team)
        current-queue-name (when queue
                             (:name queue))
        project-owner? (= (:role current-user)
                          "project-owner")
        initial-role (or role "annotator")
        initial-team-id (if project-owner?
                          (:team (first (filter #(= (str (:id %))
                                                    current-user-id)
                                                users)))
                          (or team :no-team))
        initial-team (first (filter #(= (:value %)
                                        initial-team-id)
                                    user-teams))
        role-names (get-in initial-team [:data :role-names] {:annotator "Annotator"
                                                             :senior-annotator "Senior Annotator"
                                                             :project-owner "Project Owner"
                                                             :admin "Admin"})]
    [:div {:appearance #{:column}
           :class [:admin-page__form-layout]}
     [:h4 {:class [:admin-page__header]}
      (if selected-user
        (str "Edit user: " first-name " " last-name)
        "Add user")]
     [:div.form-row.button-group
      [button/button {:appearance #{:tertiary}
                      :type :button
                      :on-click #(rf/dispatch [::handlers/select-view nil true])}
       "Back"]]
     (when created
       [:div
        [:span
         [:strong "Created "]
         (time-format/unparse time+date-formatter created)]])
     (when queue
       [:div
        [:span
         [:strong "Queue "]
         current-queue-name]])
     [:div {:class [:admin-page__forms]}
      [:div {:key id}
       [form/form
        {::form/name :user-form
         ::form/on-submit #(rf/dispatch (if id
                                          (if (and (= (:role %)
                                                      initial-role)
                                                   (= (:team %)
                                                      current-queue-team-id))
                                            [::handlers/update-user % id]
                                            [::modal-handlers/show :confirm-update-user %])
                                          [::handlers/add-user %]))}
        {:email {::form/name :email
                 ::form/label "User's Email Address"
                 ::form/initial-value email
                 ::form/disabled? (boolean id)
                 ::input/type :email
                 ::validation/spec ::spec/not-empty
                 ::validation/messages {::spec/not-empty "Must not be empty"}
                 :class [:admin-page__input]
                 :appearance #{:email-label :primary}}
         :role {::form/name :role
                ::form/initial-value initial-role
                ::form/disabled? (= (str id)
                                    current-user-id)
                ::radio/options (cond-> [{:value "annotator"
                                          :label (get role-names :annotator)}
                                         {:value "senior-annotator"
                                          :label (get role-names :senior-annotator)}
                                         {:value "project-owner"
                                          :label (get role-names :project-owner)}]
                                  (not project-owner?) (conj {:value "admin"
                                                              :label (get role-names :admin)}))
                :appearance #{:right-label}}
         :team {::form/name :team
                ::select/options user-teams
                ::form/initial-value initial-team-id
                ::form/disabled? project-owner?}
         :is-active {::form/name :is-active
                     ::form/initial-value (if selected-user is-active true)
                     ::checkbox/option true
                     ::form/disabled? true}
         :is-evaluator {::form/name :is-evaluator
                        ::form/initial-value is-evaluator
                        ::checkbox/option true
                        ::checkbox/label "Ignore edits?"}
         :require-approval? {::form/name :require-approval?
                             ::form/initial-value require-approval?
                             ::checkbox/option true
                             ::checkbox/label "Require approval for edits?"}}
        (fn [{:keys [::form/submit! ::validation/valid?] :as _form-props} {:keys [email role team is-active is-evaluator require-approval?] :as _field-props}]
          [:div
           [:form
            [:div {:class [:admin-page__row-layout]}
             (when-not id
               [input/field email])
             [:div
              "Role"
              [radio/field role]]
             [:div
              "Modifiers"
              [checkbox/field is-evaluator]
              [checkbox/field require-approval?]]
             [:span {:hidden true}
              [checkbox/field is-active]]
             (when-not project-owner?
               [:div {:class [:form__label]}
                "Team"
                [single-select/field (assoc team :appearance #{:primary})]])]
            (let [disabled? (or adding-user? updating? (not valid?))]
              [button/button {:appearance #{:primary}
                              :class [:admin-page__submit-button]
                              :id :submit-btn
                              :on-click submit!
                              :disabled disabled?}
               (if id
                 "Update user"
                 "Add user")])
            [update-user-confirmation-dialog id first-name last-name initial-role current-queue-team-id current-queue-name]]])]]]]))

(defn- list-view
  [_props]
  (let [show-inactive-users? (r/atom false)
        user-team-filter (r/atom :no-team-filter)
        ordering (r/atom nil)]
    (fn [{:keys [current-user users user-teams active-users] :as _props}]
      (let [project-owner? (= (:role current-user)
                              "project-owner")
            team-id (when project-owner?
                      (:team (first (filter #(= (str (:id %))
                                                (:id current-user))
                                            users))))
            team (when project-owner?
                   (first (filter #(= (str (:value %))
                                      (str team-id))
                                  user-teams)))
            team-name (:representation team)
            role-names (get-in team [:data :role-names] {:annotator "Annotator"
                                                         :senior-annotator "Senior Annotator"
                                                         :project-owner "Project Owner"
                                                         :admin "Admin"})
            visible-users (filter (fn [user]
                                    (let [filt @user-team-filter]
                                      (or (= :no-team-filter filt)
                                          (= filt
                                             (:team user)))))
                                  (if @show-inactive-users? users active-users))
            sorted-users (if-let [{:keys [field order]} @ordering]
                           (sort-by field
                                    (if (= order :asc)
                                      <
                                      >)
                                    visible-users)
                           visible-users)]
        [:<>
         [:div {:appearance #{:column}
                :class [:admin-page__table-layout]}
          [:h4 {:class [:admin-page__header]}
           (if project-owner?
             (str "Manatee Users: " team-name)
             "Manatee Users")]
          [:div.form-row.button-group
           [button/button {:appearance #{:tertiary}
                           :type :button
                           :on-click #(rf/dispatch [::handlers/select-view nil false])}
            "New"]]
          [:div.checkbox__container
           {:class [:manatee]}
           [:div {:class [:admin-page__form-items]}
            (when-not project-owner?
              [team-filter user-teams user-team-filter])
            [inactive-filter show-inactive-users?]]]
          [table/component
           {:appearance #{:primary}
            ::collection/items (mapv format-user-cells sorted-users)
            ::collection/update #(reset! ordering (:ordering %))
            ::table/row-on-click #(rf/dispatch [::handlers/select-view (:id %) false])
            ::collection/fields (cond-> [{::collection/id :first-name
                                          ::collection/label "First Name"}
                                         {::collection/id :last-name
                                          ::collection/label "Last Name"
                                          ::table/ordered? true}
                                         {::collection/id :id
                                          ::collection/label "Id"
                                          ::table/formatter user-id-formatter}
                                         {::collection/id :role
                                          ::collection/label "Role"
                                          ::table/formatter (partial user-role-formatter role-names)}]
                                  (not project-owner?) (conj {::collection/id :team
                                                              ::collection/label "Team"
                                                              ::table/formatter (partial user-team-formatter user-teams)})
                                  :always (concat [{::collection/id :is-evaluator
                                                    ::collection/label "Ignore edits?"}
                                                   {::collection/id :require-approval?
                                                    ::collection/label "Require approval?"}
                                                   {::collection/id :is-active
                                                    ::collection/label "Active?"
                                                    ::table/formatter (partial user-active-formatter (:id current-user))}
                                                   {::collection/id :session-end
                                                    ::collection/label "Last activity"
                                                    ::table/ordered? true}]))}]]]))))

(defn users-panel
  [props]
  (let [list-view? @(rf/subscribe [::subs/list-view?])]
    [:<>
     [panel-component/panel {:appearance #{:primary}
                             :class [:admin-page__panel]}
      (if list-view?
        [list-view props]
        [user-form-component])]]))
